import { useEffect, useState } from "react";
import axios from "axios";
import { baseApiURL } from "../../../baseUrl";
import toast from "react-hot-toast";
import { FiBook, FiSearch, FiDownload } from "react-icons/fi";
import * as XLSX from "xlsx";

const ViewLibrary = () => {
  const [librarians, setLibrarians] = useState([]);
  const [filteredLibrarians, setFilteredLibrarians] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("Ascending");

  useEffect(() => {
    const fetchLibrarians = async () => {
      try {
        const response = await axios.get(`${baseApiURL()}/library/details/getDetails2`);
        if (response.data.success) {
          setLibrarians(response.data.librarians);
          setFilteredLibrarians(response.data.librarians);
        } else {
          toast.error("Failed to load librarians");
        }
      } catch (error) {
        toast.error("Error fetching librarians");
        console.error(error);
      }
    };

    fetchLibrarians();
  }, []);

  useEffect(() => {
    let filtered = librarians.filter((librarian) => {
      const matchesSearch = searchTerm === "" || 
        librarian.libraryId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${librarian.firstName} ${librarian.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        librarian.email.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    // Sort by library ID
    filtered.sort((a, b) => {
      const aNum = parseInt(a.libraryId);
      const bNum = parseInt(b.libraryId);
      return sortOrder === "Ascending" ? aNum - bNum : bNum - aNum;
    });

    setFilteredLibrarians(filtered);
  }, [librarians, searchTerm, sortOrder]);

  const downloadExcel = () => {
    if (filteredLibrarians.length === 0) {
      toast.error("No librarians to export!");
      return;
    }

    const dataToExport = filteredLibrarians.map((librarian) => ({
      "Library ID": librarian.libraryId,
      "Name": `${librarian.firstName} ${librarian.lastName}`,
      "Email": librarian.email,
      "Phone": librarian.phoneNumber,
      "Gender": librarian.gender,
    }));

    // Create and download Excel file
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Librarians");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = "librarians_data.xlsx";
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success("Excel file downloaded successfully!");
  };

  return (
    <div className="space-y-6">
      <div className="bento-header-banner flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <FiBook className="text-indigo-600 dark:text-indigo-400" /> Library Management
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">View and manage librarian staff records</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-xs font-semibold px-2.5 py-1 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-xl border border-indigo-100 dark:border-indigo-800">
            {filteredLibrarians.length} librarians found
          </span>
          <button
            onClick={downloadExcel}
            className="flex items-center space-x-1.5 bg-indigo-600 hover:bg-indigo-500 text-white px-3.5 py-2 rounded-xl text-xs font-bold transition shadow-xs cursor-pointer"
          >
            <FiDownload className="text-xs" />
            <span>Export Excel</span>
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-6 space-y-6">
        {/* Filters Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Search Librarians
            </label>
            <div className="relative">
              <FiSearch className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, ID, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Sort Order
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
            >
              <option value="Ascending">Ascending</option>
              <option value="Descending">Descending</option>
            </select>
          </div>
        </div>

        {/* Table Section */}
        <div>
          {filteredLibrarians.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
              <FiBook className="mx-auto text-slate-400 text-3xl mb-2" />
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">No librarians found</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">Try adjusting your search criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                  <tr>
                    <th className="py-3.5 px-4 text-left">Library ID</th>
                    <th className="py-3.5 px-4 text-left">Name</th>
                    <th className="py-3.5 px-4 text-left">Email</th>
                    <th className="py-3.5 px-4 text-left">Phone</th>
                    <th className="py-3.5 px-4 text-left">Gender</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                  {filteredLibrarians.map((librarian) => (
                    <tr 
                      key={librarian.libraryId} 
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="py-3.5 px-4 font-mono font-bold text-indigo-600 dark:text-indigo-400">{librarian.libraryId}</td>
                      <td className="py-3.5 px-4 font-semibold text-slate-900 dark:text-white">
                        {librarian.firstName} {librarian.lastName}
                      </td>
                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-400">{librarian.email}</td>
                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-400">{librarian.phoneNumber}</td>
                      <td className="py-3.5 px-4">
                        <span className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${
                          librarian.gender === 'Male' 
                            ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800' 
                            : 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-100 dark:border-rose-800'
                        }`}>
                          {librarian.gender}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewLibrary;